import {
  Button,
  InputSelectControl,
  SettingsFormControl,
  SettingsGroup,
  Flex
} from '@sequencehq/core-components'

import { GreyGrey30, GreyWhite } from '@sequencehq/core-components'
import { useNavigate, useParams } from 'react-router-dom'
import { Currency } from '@sequencehq/api'
import { accountDetailsTypeDescription } from 'SettingsV2/components/bank-details/bankDetails.types'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  useMutation,
  useQuery,
  isSequenceApiError,
  isSequenceApiErrorResponse
} from '@sequencehq/api'
import { FC, useState } from 'react'
import { SettingsPageLayout } from 'SettingsV2/components/SettingsPageLayout'
import {
  getBankAccountsMap,
  validateAccountType,
  validateCurrency
} from 'SettingsV2/components/bank-details/bankDetails.utils'
import { useNotifications } from '@sequencehq/core-components'
import { usePaymentDetailsForm } from 'SettingsV2/components/bank-details/usePaymentDetailsForm'
import { BASE_INPUT_FIELD_STYLES } from 'SettingsV2/settings.constants'

export const BankDetailsEditSettingsPage: FC = () => {
  const navigate = useNavigate()

  const params = useParams<{
    currency: Currency
    accountType: keyof typeof accountDetailsTypeDescription
  }>()

  const merchant = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount
  )

  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const currency = validateCurrency(params.currency)
  const accountType = validateAccountType(params.accountType)
  const bankDetailsMap = getBankAccountsMap(merchant.data?.bankAccountDetails)
  const bankDetails = bankDetailsMap.get(`${currency?.key}_${accountType?.key}`)

  const paymentDetailsForm = usePaymentDetailsForm({
    accountType: accountType?.key,
    showValidationErrors,
    bankDetails
  })

  const { displayNotification } = useNotifications()

  const mutator = useMutation(dashboard20240730Client.putMerchant, {
    onSuccess() {
      displayNotification('Bank details updated', {
        type: 'success'
      })
      navigate('/settings/bank-details')
    },
    onError(error) {
      const description =
        isSequenceApiError(error) && isSequenceApiErrorResponse(error.response)
          ? error.response.error.errors.at(0)?.message
          : undefined

      displayNotification('Bank details failed to update', {
        type: 'error',
        description
      })
    }
  })

  const backUrl = '/settings/bank-details'

  const canSubmit =
    paymentDetailsForm?.queries.isValid &&
    paymentDetailsForm?.queries.isDirty &&
    merchant.data &&
    !mutator.isPending

  return (
    <>
      <SettingsPageLayout
        header={currency ? `${currency.key} - ${currency.name}` : ''}
        isError={!merchant.isLoading && !bankDetails}
        backButton={{
          href: backUrl,
          label: 'Bank details'
        }}
      >
        <SettingsGroup rows={2} isLoading={merchant.isLoading}>
          <SettingsFormControl label="Currency" htmlFor="currency">
            <InputSelectControl
              id="currency"
              placeholder="Currency"
              triggerStyle={{
                width: BASE_INPUT_FIELD_STYLES.width
              }}
              initialValue={currency?.key}
              disabled
              options={
                currency ? [{ value: currency.key, label: currency.name }] : []
              }
            />
          </SettingsFormControl>

          <SettingsFormControl label="Account type" htmlFor="account-type">
            <InputSelectControl
              id="account-type"
              placeholder="Account type"
              triggerStyle={{
                width: BASE_INPUT_FIELD_STYLES.width
              }}
              disabled
              initialValue={accountType?.key}
              options={
                accountType
                  ? [{ value: accountType.key, label: accountType.name }]
                  : []
              }
            />
          </SettingsFormControl>
        </SettingsGroup>

        {paymentDetailsForm?.component}
      </SettingsPageLayout>

      <Flex
        justifyContent="end"
        gap="8px"
        px="12px"
        py="8px"
        backgroundColor={GreyWhite}
        borderTop={`1px solid ${GreyGrey30}`}
        position="sticky"
        bottom={0}
      >
        <Button variant="secondary" onClick={() => navigate(backUrl)}>
          Cancel
        </Button>

        <Button
          variant="primary"
          disabled={!canSubmit}
          submitting={mutator.isPending}
          submittingText="Saving bank details..."
          onClick={() => {
            setShowValidationErrors(true)

            if (!canSubmit || !merchant.data) return
            const bankAccountDetails = merchant.data.bankAccountDetails.map(
              details => {
                if (
                  details.key === currency?.key &&
                  details.value.type === accountType?.key
                ) {
                  return {
                    ...details,
                    value: paymentDetailsForm.value
                  }
                }

                return details
              }
            )

            mutator.mutate({
              id: merchant.data.id,
              body: {
                legalCompanyName: merchant.data.legalCompanyName,
                address: merchant.data.address,
                email: merchant.data.email,
                bankAccountDetails
              }
            })
          }}
        >
          Save bank details
        </Button>
      </Flex>
    </>
  )
}
